import React from "react";

// ── Shared form styles ─────────────────────────────────────────────────────────
export const fs = {
  group: { marginBottom: "0.875rem" },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 4,
    letterSpacing: "0.02em",
  },
  input: {
    fontSize: 13,
    padding: "7px 10px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  inputDisabled: {
    background: "#f9fafb",
    color: "#6b7280",
    cursor: "not-allowed",
  },
  textarea: {
    fontSize: 13,
    padding: "7px 10px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    resize: "vertical",
    lineHeight: 1.5,
  },
  select: {
    fontSize: 13,
    padding: "7px 10px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#1a1d23",
    background: "#fff",
    width: "100%",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  currencyWrapper: {
    display: "flex",
    alignItems: "stretch",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    overflow: "hidden",
  },
  currencyPrefix: {
    padding: "7px 10px",
    background: "#f3f4f6",
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 500,
    borderRight: "1px solid #d1d5db",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
  },
  currencyInput: {
    fontSize: 13,
    padding: "7px 10px",
    border: "none",
    color: "#1a1d23",
    background: "#fff",
    flex: 1,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    minWidth: 0,
  },
  helpText: {
    fontSize: 11,
    color: "#9ca3af",
    marginTop: 3,
  },
  cardSection: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "1.25rem",
  },
  cardSectionHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
  },
  cardSectionBody: { padding: "1rem 1.125rem" },
};

// ── FormField ──────────────────────────────────────────────────────────────────
export const FormField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  min = null,
  max = null,
  step = null,
  as = null,
  rows = null,
  options = null,
  className,
  disabled = false,
  helpText = null,
}) => {
  const isSelect = type === "select";
  const isTextarea = as === "textarea";

  const baseInputStyle = {
    ...fs.input,
    ...(disabled ? fs.inputDisabled : {}),
  };
  const baseSelectStyle = {
    ...fs.select,
    ...(disabled ? fs.inputDisabled : {}),
  };
  const baseTextareaStyle = {
    ...fs.textarea,
    ...(disabled ? fs.inputDisabled : {}),
  };

  const sharedProps = { name, value, onChange, required, placeholder, disabled };

  return (
    <div style={{ ...fs.group, ...(className === "mb-0" ? { marginBottom: 0 } : {}) }}>
      <label style={fs.label}>
        {label}
        {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
      </label>
      {isSelect ? (
        <select style={baseSelectStyle} {...sharedProps}>
          {options?.map((opt, i) =>
            Array.isArray(opt.options) ? (
              <optgroup key={i} label={opt.label}>
                {opt.options.map((sub, j) => (
                  <option key={j} value={sub.value}>
                    {sub.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              <option key={i} value={opt.value}>
                {opt.label}
              </option>
            )
          )}
        </select>
      ) : isTextarea ? (
        <textarea style={baseTextareaStyle} rows={rows || 3} {...sharedProps} />
      ) : (
        <input
          style={baseInputStyle}
          type={type}
          {...sharedProps}
          {...(min !== null ? { min } : {})}
          {...(max !== null ? { max } : {})}
          {...(step !== null ? { step } : {})}
        />
      )}
      {helpText && <div style={fs.helpText}>{helpText}</div>}
    </div>
  );
};

// ── CurrencyField ──────────────────────────────────────────────────────────────
export const CurrencyField = ({
  label,
  name,
  value,
  onChange,
  placeholder = "0.00",
  className,
  required = false,
  disabled = false,
}) => (
  <div style={{ ...fs.group, ...(className === "mb-0" ? { marginBottom: 0 } : {}) }}>
    <label style={fs.label}>
      {label}
      {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </label>
    <div style={{ ...fs.currencyWrapper, ...(disabled ? { background: "#f9fafb" } : {}) }}>
      <span style={fs.currencyPrefix}>TTD</span>
      <input
        style={{ ...fs.currencyInput, ...(disabled ? { background: "#f9fafb", color: "#6b7280" } : {}) }}
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        min="0"
        step="0.01"
        placeholder={placeholder}
        required={required}
        disabled={disabled}
      />
    </div>
  </div>
);

// ── CardSection ────────────────────────────────────────────────────────────────
export const CardSection = ({ title, children }) => (
  <div style={fs.cardSection}>
    <div style={fs.cardSectionHead}>{title}</div>
    <div style={fs.cardSectionBody}>{children}</div>
  </div>
);

// ── CheckboxGroup ──────────────────────────────────────────────────────────────
export const CheckboxItem = ({ id, label, value, checked, onChange }) => (
  <label
    htmlFor={id}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 7,
      fontSize: 13,
      color: "#374151",
      cursor: "pointer",
      userSelect: "none",
    }}
  >
    <input
      id={id}
      type="checkbox"
      value={value}
      checked={checked}
      onChange={onChange}
      style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#1E40AF" }}
    />
    {label}
  </label>
);
